import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { LeftOutlined } from '../../components/icons'
import { Chip } from '../../components/ui/Chip'
import { EmptyState } from '../../components/ui/EmptyState'
import { Icon } from '../../components/ui/Icon'
import { apiClient } from '../../lib/api/client'
import { displayNameFor } from '../../lib/fixtures/sourceable-lookup'
import { RfqBundledCallout } from './RfqBundledCallout'
import { RfqCard } from './RfqCard'
import { RfqDetailServicesCard } from './RfqDetailServicesCard'
import { RfqDetailSummaryRail } from './RfqDetailSummaryRail'
import {
  RFQ_STATUS_CHIP,
  rfqIsQuoted,
  rfqNumberDisplay,
  rfqPartsMetaLine,
  rfqSubmittedQuotedLabel,
} from './domain'
import type { QuoteVariant, Rfq, RfqLineView } from './domain'

type LoadState = 'loading' | 'loaded' | 'not-found' | 'error'

/**
 * UID-135 (ADR-0056) — the RFQ detail screen at `/rfq/:rfqId`, modelled
 * line-for-line on `OrderDetailPage` (`/orders/:orderId`): a route-level
 * fetch keyed on the `:rfqId` PATH PARAM ONLY (loading / loaded / not-found
 * / error), a "← Back" link, and the full existing `RfqCard` body reused
 * verbatim as the interim detail content (ADR-0056 §D1 — the richer
 * detail-body restructuring is a follow-on).
 *
 * SECURITY (IDOR, ADR-0056 Decision 6 — Gate 5 will check this): this fetch
 * reads `GET /rfq/{rfqId}` using ONLY `useParams()`'s `rfqId`. It never
 * accepts, forwards, or reconstructs a tenant/account id from the URL,
 * query string, router state, or any other client input — the server
 * resolves the tenant from the session and returns 404 for a cross-tenant
 * id, indistinguishable from an unknown one (ADR-0002/0009). There is no
 * client-side "does this belong to me?" check here, by design.
 *
 * Self-sufficient by design (ADR-0056 Decision 2): a direct deep link, a
 * shared URL, or a browser refresh must all work with only the `:rfqId` in
 * hand — this page does NOT trust an `Rfq` object passed via router state
 * or read `RfqListPage`'s in-memory list; it always re-fetches.
 */
export function RfqDetailPage() {
  const { rfqId } = useParams<{ rfqId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [loadState, setLoadState] = useState<LoadState>('loading')
  const [rfq, setRfq] = useState<Rfq | undefined>()
  const [liveMessage, setLiveMessage] = useState('Loading RFQ.')
  // UID-238 item 2 (ADR-0056 amendment) — the pricing-region variant
  // selection, LIFTED here so the co-mounted `RfqCard` (its `QuotedLinesPanel`
  // picker) and `RfqDetailSummaryRail` (its Region/Total display) share one
  // source of truth instead of each independently seeding "the first
  // variant" and never seeing the other's selection. Reset whenever the
  // route's `:rfqId` changes so a different RFQ never inherits a stale pick.
  const [selectedVariantId, setSelectedVariantId] = useState<QuoteVariant['id'] | undefined>(
    undefined,
  )
  const headingRef = useRef<HTMLHeadingElement | null>(null)
  // code-review fix: guards the heading-focus effect below to the INITIAL
  // load only. Without this, an action-triggered reload (Checkout/Resubmit
  // -> onChanged() -> load() -> loading -> loaded) re-ran the same effect
  // and yanked focus back to the <h1>, away from wherever the user actually
  // was (e.g. a toast, or the button they just clicked) — a real WCAG 2.4.3
  // regression on every subsequent action, not just first navigation.
  const didInitialFocusRef = useRef(false)

  const load = useCallback(async () => {
    if (!rfqId) return
    setLoadState('loading')
    setLiveMessage('Loading RFQ.')
    try {
      const { data, error } = await apiClient.GET('/rfq/{rfqId}', {
        params: { path: { rfqId } },
      })
      if (data) {
        setRfq(data)
        setLoadState('loaded')
        setLiveMessage('RFQ loaded.')
        return
      }
      const status = (error as { status?: number } | undefined)?.status
      if (status === 401) {
        return // Handled globally by authMiddleware's session invalidation.
      }
      if (status === 404) {
        setLoadState('not-found')
      } else {
        setLoadState('error')
      }
      setLiveMessage('RFQ failed to load.')
    } catch {
      setLoadState('error')
      setLiveMessage('RFQ failed to load.')
    }
  }, [rfqId])

  useEffect(() => {
    void load()
  }, [load])

  // UID-238 item 2: reset the lifted variant selection on a route change
  // (a different `:rfqId`) — never carry one RFQ's pricing-region pick over
  // to another. `rfqId` is deliberately the only dependency: the effect
  // body doesn't read it, but its whole purpose is to re-run WHEN it
  // changes (`setSelectedVariantId` is a stable setter).
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — see comment above.
  useEffect(() => {
    setSelectedVariantId(undefined)
  }, [rfqId])

  // UX spec §1.3 (WCAG 2.4.3/3.2.5): move focus to the detail heading on the
  // INITIAL successful navigation only, resetting the tab order to the top
  // exactly like a full page navigation would. A later reload triggered by
  // an in-page action (Checkout/Resubmit's `onChanged()`) must NOT re-focus
  // the heading — that would silently steal focus from wherever the user
  // legitimately is after their action.
  useEffect(() => {
    if (loadState === 'loaded' && !didInitialFocusRef.current) {
      didInitialFocusRef.current = true
      headingRef.current?.focus()
    }
  }, [loadState])

  // Perf (no behavior change) — `RfqCard` is now `React.memo`-wrapped
  // (RfqCard.tsx); that memoization only holds if the callback PROPS handed
  // to it keep a stable identity across this page's re-renders. `load` is
  // already `useCallback`-stable (deps `[rfqId]`) above, so this only needs
  // to wrap the inline arrow that closes over it. `onVariantSelect` is NOT
  // wrapped here — `setSelectedVariantId` (passed directly below) is a
  // `useState` setter, which React already guarantees is referentially
  // stable across renders, so wrapping it would be a no-op.
  // UID-611 (ADR-0179 D2/ADR-0029) — `RfqCard`'s `onChanged` now optionally
  // carries the fresh, already-derived `RfqView` a quantity-PATCH/line
  // removal response returns; hydrate from it directly (mirroring
  // `RfqDetailSummaryRail`'s own `onChanged` below) rather than paying for a
  // second `GET` and risking a `loading` flash between the mutation and the
  // re-render. Called with no argument for every other existing action
  // (accept-and-add-to-cart, re-request), which still refetches, unchanged.
  const handleRfqCardChanged = useCallback(
    (fresh?: Rfq) => {
      if (fresh) {
        setRfq(fresh)
        return
      }
      void load()
    },
    [load],
  )

  // UID-611 (ADR-0179 D3) — a line removal (single or batch) that took the
  // RFQ's last remaining line cascade-deletes the RFQ itself server-side;
  // `RfqCard` has no route knowledge of its own, so this page navigates
  // away from `/rfq/:rfqId` back to the RFQ list rather than attempting to
  // re-render a now-deleted resource. No confirmation dialog before or
  // after — ADR-0179 D3's explicit, product-authorized posture.
  const handleRfqDeleted = useCallback(() => {
    navigate('/rfq')
  }, [navigate])

  // Perf (no behavior change) — same reasoning as `handleRfqCardChanged`
  // above; `navigate` (from `useNavigate`) is itself referentially stable,
  // but the inline arrow wrapping it was not.
  const handleUploadNewFiles = useCallback(() => {
    navigate('/uploads')
  }, [navigate])

  // ADR-0056 Decision 1: `navigate(-1)` for the natural Back (pops to the
  // `/rfq` entry with its tab/scroll state intact, per RfqListRow's
  // `replace` navigate before it pushed here); falls back to a fresh
  // `/rfq` navigation when there is no history entry to pop — a direct/
  // bookmarked deep link, where `location.key === 'default'` (no prior
  // list position exists to restore, so the default tab/top-of-list is
  // correct).
  function handleBack() {
    // `location.key === 'default'` means this entry has no predecessor in
    // THIS session's history stack (a fresh/bookmarked/shared deep link) —
    // `navigate(-1)` would leave the app entirely in that case.
    if (location.key !== 'default') {
      navigate(-1)
    } else {
      navigate('/rfq')
    }
  }

  if (!rfqId) {
    return null
  }

  return (
    <section aria-labelledby="rfq-detail-heading">
      <p aria-live="polite" className="sr-only">
        {liveMessage}
      </p>

      {loadState === 'loading' && <p>Loading this RFQ…</p>}

      {loadState === 'not-found' && (
        <EmptyState
          title="We couldn't find this RFQ"
          action={
            <Link to="/rfq" className="btn-link">
              Back to RFQ
            </Link>
          }
        />
      )}

      {loadState === 'error' && (
        <div role="alert" className="table-error-panel">
          <p>Couldn&rsquo;t load this RFQ.</p>
          <button type="button" className="btn-link" onClick={() => void load()}>
            Try again
          </button>
        </div>
      )}

      {loadState === 'loaded' && rfq && (
        <>
          <button type="button" className="back-link rfq-detail-back-link" onClick={handleBack}>
            {/* UID-325 (ADR-0094 D2 amendment) — REVERTS to the design's bare
             * "Back" (`Fictiv Platform.dc.html`, RFQ detail header). D2 chose
             * "Back to RFQ" to name the destination; the live browser diff
             * measured the design's own control as a 49px-wide "Back", and
             * this ticket's ruling is design-fidelity-first. The destination
             * is unambiguous from context here (the page's own <h1> is the
             * RFQ number), so nothing is lost for screen-reader users. */}
            <LeftOutlined aria-hidden="true" /> Back
          </button>
          {/* UID-294 §1 (ADR-0104 D4, `Fictiv Platform.dc.html:3507`) — the
           * 44x44 lavender document-icon chip immediately left of the <h1>.
           * Purely presentational/decorative (aria-hidden): the <h1> itself
           * remains the sole accessible name for this heading. */}
          <div className="rfq-detail-heading-row">
            <span className="rfq-detail-heading-icon" aria-hidden="true">
              <Icon name="document" />
            </span>
            {/* UID-325 (AC5) — `dc.html:3508-3514`: the icon chip's sibling is
             * a COLUMN holding the title row and the subheading, so both are
             * indented to x=332 under the `<h1>`. Previously the `<h1>` sat
             * alone in this row and the subheading was a sibling of the row
             * itself, leaving it back at the x=274 page gutter. */}
            <div className="rfq-detail-heading-block">
              <div className="rfq-detail-heading-title-row">
                <h1
                  id="rfq-detail-heading"
                  className="display-heading"
                  ref={headingRef}
                  tabIndex={-1}
                >
                  {/* UID-289 (ADR-0093 §C1) — the server-authored sequential RFQ
                   * number, falling back to the pre-ADR-0093 UUID-fragment format
                   * when absent (an older/edge-case response with no derivable
                   * rank) rather than a raw, unformatted id. */}
                  {rfqNumberDisplay(rfq)}
                </h1>
                {/* UID-325 (AC5) — the status badge is INLINE, right of the RFQ
                 * number (`dc.html:3511`). It used to render inside
                 * `RfqCard`'s own header, which put it on its own row below
                 * the subheading. Read straight off the pure
                 * `RFQ_STATUS_CHIP` lookup rather than lifting anything out of
                 * `RfqCard`: no hook, no second fetch. The handoff sub-state
                 * chips ("Ready to share" / "Share link expired") stay in
                 * `RfqCard` — they are a UID-54 addition with no counterpart
                 * in this design row, and moving them would mean a third
                 * `useShareHandoff` call on one screen. */}
                <Chip className="rfq-chip" tone={RFQ_STATUS_CHIP[rfq.status].tone}>
                  {RFQ_STATUS_CHIP[rfq.status].label}
                </Chip>
              </div>
              {/* UID-289 (ADR-0093 content-rendering) — the combined subheading
               * meta line: part name · process/qty/partNumber · Submitted/
               * Quoted dates (`Fictiv Platform.dc.html` ~L3514:
               * `{{r.name}} · {{r.metaLabel}} · Submitted {{r.submitted}}
               * [· Quoted {{r.returned}}]`). */}
              <p className="rfq-detail-subheading caption-text">
                {[
                  (rfq.lines as readonly RfqLineView[])[0]?.part?.name ??
                    (rfq.lines[0] ? displayNameFor(rfq.lines[0].sourceable) : null),
                  rfqPartsMetaLine(rfq.lines as readonly RfqLineView[]),
                  rfqSubmittedQuotedLabel(rfq),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
          </div>
          <div className="rfq-detail-layout">
            <div className="rfq-detail-main">
              <RfqCard
                rfq={rfq}
                onChanged={handleRfqCardChanged}
                onRfqDeleted={handleRfqDeleted}
                selectedVariantId={selectedVariantId}
                onVariantSelect={setSelectedVariantId}
                // UID-294 — the "Add parts to this RFQ" modal's "Upload new
                // files" footer hands off to the CAD-intake screen.
                onUploadNewFiles={handleUploadNewFiles}
              />
              {/* UID-171 (ADR-0062) — the requested-services recap, read from the
                  RfqView projection. Positioned below the parts table (which lives
                  inside RfqCard, left untouched per this ticket's scope); it omits
                  itself entirely when there are zero/unsurfaced services. */}
              <RfqDetailServicesCard
                services={rfq.requestedServices}
                quoted={rfqIsQuoted(rfq.status)}
              />
              {/* UID-198 (Gate 2 UX spec §3.3, ADR-0074) — the always-visible
                  bundled-quote callout. Mounted here (not inside RfqCard) so it
                  renders below the services section per the acceptance
                  criterion; gated on rfq.bundled alone, independent of
                  quote-load state. */}
              {rfq.bundled === true && <RfqBundledCallout />}
            </div>
            <RfqDetailSummaryRail
              rfq={rfq}
              /* UID-265b/265d (ADR-0093 D3) — the append and the resubmit both
               * answer `200` + the whole refreshed `RfqView`, so hydrate from
               * that response directly instead of paying for a second `GET`.
               * Also avoids the `loading` flash a full `load()` would put
               * between the mutation and the re-render. Every other action
               * (Checkout) still calls `onChanged()` with no argument and
               * refetches, unchanged. */
              onChanged={(fresh) => {
                if (fresh) {
                  setRfq(fresh)
                  return
                }
                void load()
              }}
              selectedVariantId={selectedVariantId}
              onVariantSelect={setSelectedVariantId}
            />
          </div>
        </>
      )}
    </section>
  )
}
